import { SiteHeader } from "@/components/site-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  type ConfigEntry,
  type ConfigEnv,
  configsProvider,
} from "@/providers/configs.provider";
import {
  IconArrowBackUp,
  IconArrowLeft,
  IconBraces,
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconClock,
  IconGitCompare,
  IconRefresh,
  IconRocket,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { diffLines } from "diff";
import { useState } from "react";
import { toast } from "sonner";

// ── Types ─────────────────────────────────────────────────────────────────────

type EnvFilter = "staging" | "prod" | "both";
type SheetState = { open: false } | { open: true; configType: string };

// ── Helpers ───────────────────────────────────────────────────────────────────

const ENV_STYLES: Record<ConfigEnv, string> = {
  staging:
    "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400",
  prod: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
};

function EnvBadge({ env }: { env: ConfigEnv }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${ENV_STYLES[env]}`}
    >
      {env}
    </span>
  );
}

function VersionBadge({ version }: { version: number }) {
  return (
    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-mono font-medium">
      v{version}
    </span>
  );
}

function PublicBadge({ isPublic }: { isPublic: boolean }) {
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-xs font-medium",
        isPublic
          ? "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-400"
          : "bg-muted text-muted-foreground",
      )}
    >
      {isPublic ? "public" : "private"}
    </span>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

// ── JSON syntax highlight ─────────────────────────────────────────────────────

type TokenType =
  | "key"
  | "string"
  | "number"
  | "boolean"
  | "null"
  | "punctuation"
  | "whitespace";

const TOKEN_CLASSES: Record<TokenType, string> = {
  key: "text-blue-500 dark:text-blue-400",
  string: "text-emerald-600 dark:text-emerald-400",
  number: "text-amber-600 dark:text-amber-400",
  boolean: "text-violet-600 dark:text-violet-400",
  null: "text-rose-500 dark:text-rose-400",
  punctuation: "text-muted-foreground",
  whitespace: "",
};

function tokenizeJson(json: string): Array<{ type: TokenType; value: string }> {
  const tokens: Array<{ type: TokenType; value: string }> = [];
  const re =
    /"(\\.|[^"\\])*"|true|false|null|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|[{}[\],:]|\s+/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(json)) !== null) {
    const val = match[0];
    let type: TokenType;
    if (/^\s/.test(val)) {
      type = "whitespace";
    } else if (val.startsWith('"')) {
      const after = json.slice(re.lastIndex).trimStart();
      type = after.startsWith(":") ? "key" : "string";
    } else if (/^-?\d/.test(val)) {
      type = "number";
    } else if (val === "true" || val === "false") {
      type = "boolean";
    } else if (val === "null") {
      type = "null";
    } else {
      type = "punctuation";
    }
    tokens.push({ type, value: val });
  }
  return tokens;
}

function JsonBlock({
  value,
  className,
}: {
  value: Record<string, unknown>;
  className?: string;
}) {
  const json = JSON.stringify(value, null, 2);
  const tokens = tokenizeJson(json);
  return (
    <pre
      className={cn(
        "text-xs font-mono overflow-auto whitespace-pre-wrap break-all",
        className,
      )}
    >
      {tokens.map((token, i) =>
        token.type === "whitespace" ? (
          token.value
        ) : (
          <span key={i} className={TOKEN_CLASSES[token.type]}>
            {token.value}
          </span>
        ),
      )}
    </pre>
  );
}

// ── Diff view ─────────────────────────────────────────────────────────────────

function DiffView({
  from,
  to,
}: {
  from: Record<string, unknown>;
  to: Record<string, unknown>;
}) {
  const jsonFrom = JSON.stringify(from, null, 2);
  const jsonTo = JSON.stringify(to, null, 2);
  const parts = diffLines(jsonFrom, jsonTo);

  const lines: Array<{
    text: string;
    type: "added" | "removed" | "unchanged";
  }> = [];
  for (const part of parts) {
    const partLines = part.value.split("\n");
    if (partLines[partLines.length - 1] === "") partLines.pop();
    for (const line of partLines) {
      lines.push({
        text: line,
        type: part.added ? "added" : part.removed ? "removed" : "unchanged",
      });
    }
  }

  const hasChanges = lines.some((l) => l.type !== "unchanged");

  if (!hasChanges) {
    return (
      <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
        Payloads are identical.
      </div>
    );
  }

  return (
    <pre className="text-xs font-mono overflow-auto">
      {lines.map((line, i) => (
        <div
          key={i}
          className={cn(
            "px-3 leading-5",
            line.type === "added" &&
              "bg-green-200 dark:bg-green-950/70 text-green-700 dark:text-green-400",
            line.type === "removed" &&
              "bg-red-200 dark:bg-red-950/70 text-red-700 dark:text-red-400",
          )}
        >
          <span className="select-none mr-2 opacity-40 w-3 inline-block">
            {line.type === "added" ? "+" : line.type === "removed" ? "-" : " "}
          </span>
          {line.text}
        </div>
      ))}
    </pre>
  );
}

type DiffDialogState =
  | { open: false }
  | { open: true; from: ConfigEntry; to: ConfigEntry };

function ConfigDiffDialog({
  state,
  onClose,
}: {
  state: DiffDialogState;
  onClose: () => void;
}) {
  return (
    <Dialog open={state.open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[80vh] max-w-[calc(100%-2rem)] flex-col gap-0 p-0 sm:max-w-3xl">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b">
          <DialogTitle className="flex items-center gap-2 font-mono text-sm">
            {state.open && (
              <>
                <VersionBadge version={state.from.version} />
                <span className="text-muted-foreground">→</span>
                <VersionBadge version={state.to.version} />
                <span className="text-xs text-muted-foreground font-sans font-normal ml-1">
                  (current)
                </span>
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto rounded-b-lg">
          {state.open && (
            <DiffView from={state.from.payload} to={state.to.payload} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Config type row ───────────────────────────────────────────────────────────

function ConfigTypeRow({
  configType,
  envFilter,
  onSelect,
}: {
  configType: string;
  envFilter: EnvFilter;
  onSelect: () => void;
}) {
  const showStaging = envFilter === "staging" || envFilter === "both";
  const showProd = envFilter === "prod" || envFilter === "both";

  const { data: stagingConfig, isLoading: stagingLoading } = useQuery({
    queryKey: ["config-latest", configType, "staging"],
    queryFn: () => configsProvider.getLatest(configType, "staging"),
    enabled: showStaging,
    staleTime: 30_000,
  });

  const { data: prodConfig, isLoading: prodLoading } = useQuery({
    queryKey: ["config-latest", configType, "prod"],
    queryFn: () => configsProvider.getLatest(configType, "prod"),
    enabled: showProd,
    staleTime: 30_000,
  });

  return (
    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onSelect}>
      <TableCell className="font-mono font-medium">{configType}</TableCell>
      {showStaging && (
        <TableCell>
          {stagingLoading ? (
            <Skeleton className="h-4 w-16" />
          ) : stagingConfig ? (
            <div className="flex items-center gap-1.5">
              <VersionBadge version={stagingConfig.version} />
              <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                {stagingConfig.metadata.commit_message}
              </span>
            </div>
          ) : (
            <span className="text-muted-foreground text-xs italic">—</span>
          )}
        </TableCell>
      )}
      {showProd && (
        <TableCell>
          {prodLoading ? (
            <Skeleton className="h-4 w-16" />
          ) : prodConfig ? (
            <div className="flex items-center gap-1.5">
              <VersionBadge version={prodConfig.version} />
              <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                {prodConfig.metadata.commit_message}
              </span>
            </div>
          ) : (
            <span className="text-muted-foreground text-xs italic">—</span>
          )}
        </TableCell>
      )}
      <TableCell className="w-8">
        <IconChevronRight className="size-4 text-muted-foreground" />
      </TableCell>
    </TableRow>
  );
}

// ── Publish form ──────────────────────────────────────────────────────────────

function PublishForm({
  configType,
  env,
  currentPayload,
  currentPublic,
  onBack,
  onSuccess,
}: {
  configType: string;
  env: ConfigEnv;
  currentPayload: Record<string, unknown> | null;
  currentPublic: boolean | null;
  onBack: () => void;
  onSuccess: () => void;
}) {
  const [payloadText, setPayloadText] = useState(
    currentPayload ? JSON.stringify(currentPayload, null, 2) : "",
  );
  const [commitMessage, setCommitMessage] = useState("");
  const [isPublic, setIsPublic] = useState(currentPublic ?? false);
  const [parseError, setParseError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const publishMutation = useMutation({
    mutationFn: () => {
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(payloadText);
      } catch {
        throw new Error("Invalid JSON payload");
      }
      return configsProvider.publish(configType, env, {
        payload: parsed,
        commit_message: commitMessage,
        public: isPublic,
      });
    },
    onSuccess: (entry) => {
      queryClient.invalidateQueries({
        queryKey: ["config-latest", configType, env],
      });
      queryClient.invalidateQueries({
        queryKey: ["config-history", configType, env],
      });
      toast.success(`Published v${entry.version}`);
      onSuccess();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setParseError(null);
    try {
      JSON.parse(payloadText);
    } catch {
      setParseError("Invalid JSON — fix the syntax before publishing.");
      return;
    }
    publishMutation.mutate();
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-4">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground w-fit"
      >
        <IconArrowLeft className="size-4" />
        Back
      </button>

      <div className="flex items-center gap-2">
        <p className="text-sm font-semibold">Publish new version</p>
        <EnvBadge env={env} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="payload">Payload (JSON)</Label>
        <textarea
          id="payload"
          value={payloadText}
          onChange={(e) => setPayloadText(e.target.value)}
          className="font-mono text-xs rounded-lg border bg-muted/30 p-3 resize-y h-64 w-full focus:outline-none focus:ring-2 focus:ring-ring"
          placeholder="{}"
          required
        />
        {parseError && <p className="text-xs text-destructive">{parseError}</p>}
      </div>

      <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
        <div className="flex flex-col gap-0.5">
          <Label htmlFor="public-toggle">Public</Label>
          <p className="text-xs text-muted-foreground">
            Exposes this version on the unauthenticated read endpoint.
          </p>
        </div>
        <Switch
          id="public-toggle"
          checked={isPublic}
          onCheckedChange={setIsPublic}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="commit-message">Commit message</Label>
        <Input
          id="commit-message"
          placeholder="What changed and why?"
          value={commitMessage}
          onChange={(e) => setCommitMessage(e.target.value)}
          required
        />
      </div>

      <Button
        type="submit"
        disabled={publishMutation.isPending || !commitMessage.trim()}
      >
        {publishMutation.isPending ? "Publishing…" : "Publish"}
      </Button>
    </form>
  );
}

// ── Config env panel ──────────────────────────────────────────────────────────

function ConfigEnvPanel({
  configType,
  env,
}: {
  configType: string;
  env: ConfigEnv;
}) {
  const [view, setView] = useState<"detail" | "publish">("detail");
  const [expandedVersions, setExpandedVersions] = useState<Set<number>>(
    new Set(),
  );
  const [pendingRollback, setPendingRollback] = useState<ConfigEntry | null>(
    null,
  );
  const [rollbackCommit, setRollbackCommit] = useState("");
  const [pendingPromote, setPendingPromote] = useState(false);
  const [promoteCommit, setPromoteCommit] = useState("");
  const [diffState, setDiffState] = useState<DiffDialogState>({ open: false });
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ["config-latest", configType, env],
    queryFn: () => configsProvider.getLatest(configType, env),
    staleTime: 30_000,
  });

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ["config-history", configType, env],
    queryFn: () => configsProvider.getHistory(configType, env),
    enabled: !!config,
    staleTime: 30_000,
  });

  const rollbackMutation = useMutation({
    mutationFn: (entry: ConfigEntry) =>
      configsProvider.rollback(configType, env, entry.version, {
        commit_message: rollbackCommit,
      }),
    onSuccess: (entry) => {
      queryClient.invalidateQueries({
        queryKey: ["config-latest", configType, env],
      });
      queryClient.invalidateQueries({
        queryKey: ["config-history", configType, env],
      });
      toast.success(`Rolled back — now v${entry.version}`);
      setPendingRollback(null);
      setRollbackCommit("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const promoteMutation = useMutation({
    mutationFn: () =>
      configsProvider.promote(configType, { commit_message: promoteCommit }),
    onSuccess: (entry) => {
      queryClient.invalidateQueries({
        queryKey: ["config-latest", configType, "prod"],
      });
      queryClient.invalidateQueries({
        queryKey: ["config-history", configType, "prod"],
      });
      toast.success(`Promoted to prod v${entry.version}`);
      setPendingPromote(false);
      setPromoteCommit("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (view === "publish") {
    return (
      <PublishForm
        configType={configType}
        env={env}
        currentPayload={config?.payload ?? null}
        currentPublic={config?.public ?? null}
        onBack={() => setView("detail")}
        onSuccess={() => setView("detail")}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Action buttons */}
      <div className="flex items-center gap-2 justify-end">
        {env === "staging" && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={!config || promoteMutation.isPending}
            onClick={() => setPendingPromote(true)}
          >
            <IconRocket className="size-3.5" />
            Promote to prod
          </Button>
        )}
        <Button size="sm" onClick={() => setView("publish")}>
          Publish new version
        </Button>
      </div>

      {/* Current config */}
      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      ) : !config ? (
        <div className="flex flex-col items-center justify-center gap-3 py-12 rounded-lg border border-dashed text-muted-foreground">
          <IconBraces className="size-8 opacity-40" />
          <p className="text-sm">
            No config published for <EnvBadge env={env} /> yet.
          </p>
          <Button size="sm" onClick={() => setView("publish")}>
            Publish first version
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="rounded-lg border px-3 py-2.5 flex flex-col gap-1.5">
            <div className="flex items-center gap-2 flex-wrap">
              <VersionBadge version={config.version} />
              <EnvBadge env={env} />
              <PublicBadge isPublic={config.public} />
              {config.metadata.rollback_of != null && (
                <span className="rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 px-2 py-0.5 text-xs font-medium">
                  rollback of v{config.metadata.rollback_of}
                </span>
              )}
              {config.metadata.promoted_from != null && (
                <span className="rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 px-2 py-0.5 text-xs font-medium">
                  promoted from staging v{config.metadata.promoted_from}
                </span>
              )}
            </div>
            <p className="text-sm font-medium">
              {config.metadata.commit_message}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatDate(config.metadata.created_at)} ·{" "}
              {config.metadata.author_id}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">
              Payload
            </p>
            <JsonBlock
              value={config.payload}
              className="rounded-lg border bg-muted/30 p-3 max-h-80"
            />
          </div>
        </div>
      )}

      {/* History */}
      {config && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <IconClock className="size-3.5 text-muted-foreground" />
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              History
            </p>
          </div>

          {historyLoading ? (
            <div className="flex flex-col gap-1.5">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : !history?.length ? (
            <p className="text-xs text-muted-foreground italic py-2">
              No history yet.
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              {history.map((entry) => {
                const isExpanded = expandedVersions.has(entry.version);
                const toggle = () =>
                  setExpandedVersions((prev) => {
                    const next = new Set(prev);
                    next.has(entry.version)
                      ? next.delete(entry.version)
                      : next.add(entry.version);
                    return next;
                  });

                return (
                  <div
                    key={entry.id}
                    className={`rounded-lg border ${
                      entry.version === config.version
                        ? "border-primary/50 bg-primary/5"
                        : ""
                    }`}
                  >
                    <div className="flex items-start gap-2 px-3 py-2">
                      <button
                        type="button"
                        onClick={toggle}
                        className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
                      >
                        {isExpanded ? (
                          <IconChevronUp className="size-3.5" />
                        ) : (
                          <IconChevronDown className="size-3.5" />
                        )}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <VersionBadge version={entry.version} />
                          <PublicBadge isPublic={entry.public} />
                          {entry.metadata.rollback_of != null && (
                            <span className="text-xs text-amber-600 dark:text-amber-400">
                              rollback of v{entry.metadata.rollback_of}
                            </span>
                          )}
                          {entry.metadata.promoted_from != null && (
                            <span className="text-xs text-blue-600 dark:text-blue-400">
                              promoted
                            </span>
                          )}
                        </div>
                        <p className="text-xs font-medium mt-0.5 truncate">
                          {entry.metadata.commit_message}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(entry.metadata.created_at)}
                        </p>
                      </div>
                      {entry.version !== config.version && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 text-xs h-7"
                            onClick={() =>
                              setDiffState({
                                open: true,
                                from: entry,
                                to: config,
                              })
                            }
                          >
                            <IconGitCompare className="size-3" />
                            Diff
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 text-xs h-7"
                            onClick={() => {
                              setPendingRollback(entry);
                              setRollbackCommit("");
                            }}
                          >
                            <IconArrowBackUp className="size-3" />
                            Rollback
                          </Button>
                        </div>
                      )}
                    </div>
                    {isExpanded && (
                      <JsonBlock
                        value={entry.payload}
                        className="border-t bg-muted/30 px-3 py-2.5 max-h-60 rounded-b-lg"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Rollback dialog */}
      <AlertDialog
        open={pendingRollback !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingRollback(null);
            setRollbackCommit("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Rollback to v{pendingRollback?.version}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              A new version will be created with the payload from v
              {pendingRollback?.version}. This creates an audit trail entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1 pb-1 flex flex-col gap-1.5">
            <Label htmlFor="rollback-commit">Commit message</Label>
            <Input
              id="rollback-commit"
              placeholder={`Reverting to v${pendingRollback?.version}`}
              value={rollbackCommit}
              onChange={(e) => setRollbackCommit(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={rollbackMutation.isPending || !rollbackCommit.trim()}
              onClick={() =>
                pendingRollback && rollbackMutation.mutate(pendingRollback)
              }
            >
              {rollbackMutation.isPending ? "Rolling back…" : "Rollback"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diff dialog */}
      <ConfigDiffDialog
        state={diffState}
        onClose={() => setDiffState({ open: false })}
      />

      {/* Promote dialog */}
      <AlertDialog
        open={pendingPromote}
        onOpenChange={(open) => {
          if (!open) {
            setPendingPromote(false);
            setPromoteCommit("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Promote staging to prod?</AlertDialogTitle>
            <AlertDialogDescription>
              Staging v{config?.version} will be published to prod as a new
              version. The audit trail will record which staging version was
              promoted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1 pb-1 flex flex-col gap-1.5">
            <Label htmlFor="promote-commit">Commit message</Label>
            <Input
              id="promote-commit"
              placeholder="Promoting staging to prod"
              value={promoteCommit}
              onChange={(e) => setPromoteCommit(e.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={promoteMutation.isPending || !promoteCommit.trim()}
              onClick={() => promoteMutation.mutate()}
            >
              {promoteMutation.isPending ? "Promoting…" : "Promote to prod"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Config detail sheet ───────────────────────────────────────────────────────

function ConfigDetailSheet({
  configType,
  envFilter,
}: {
  configType: string;
  envFilter: EnvFilter;
}) {
  const showBothTabs = envFilter === "both";
  const defaultEnv: ConfigEnv = envFilter === "prod" ? "prod" : "staging";
  const [activeEnv, setActiveEnv] = useState<ConfigEnv>(defaultEnv);

  return (
    <>
      <SheetHeader className="px-6 pt-6 shrink-0">
        <SheetTitle className="font-mono">{configType}</SheetTitle>
        <SheetDescription>
          Config ledger — append-only, versioned
        </SheetDescription>
      </SheetHeader>

      {showBothTabs ? (
        <Tabs
          value={activeEnv}
          onValueChange={(v) => setActiveEnv(v as ConfigEnv)}
          className="flex flex-col flex-1 overflow-hidden px-6 pb-6 mt-4"
        >
          <TabsList className="w-fit shrink-0">
            <TabsTrigger value="staging">Staging</TabsTrigger>
            <TabsTrigger value="prod">Prod</TabsTrigger>
          </TabsList>
          <TabsContent value="staging" className="flex-1 overflow-auto mt-3">
            <ConfigEnvPanel configType={configType} env="staging" />
          </TabsContent>
          <TabsContent value="prod" className="flex-1 overflow-auto mt-3">
            <ConfigEnvPanel configType={configType} env="prod" />
          </TabsContent>
        </Tabs>
      ) : (
        <div className="flex-1 overflow-auto">
          <ConfigEnvPanel configType={configType} env={defaultEnv} />
        </div>
      )}
    </>
  );
}

// ── New config dialog ─────────────────────────────────────────────────────────

function NewConfigDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (configType: string) => void;
}) {
  const [typeName, setTypeName] = useState("");
  const [env, setEnv] = useState<ConfigEnv>("staging");
  const [payloadText, setPayloadText] = useState("{}");
  const [commitMessage, setCommitMessage] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  function reset() {
    setTypeName("");
    setEnv("staging");
    setPayloadText("{}");
    setCommitMessage("");
    setIsPublic(false);
    setParseError(null);
  }

  const publishMutation = useMutation({
    mutationFn: () => {
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(payloadText);
      } catch {
        throw new Error("Invalid JSON payload");
      }
      return configsProvider.publish(typeName.trim(), env, {
        payload: parsed,
        commit_message: commitMessage,
        public: isPublic,
      });
    },
    onSuccess: (entry) => {
      queryClient.invalidateQueries({ queryKey: ["config-types"] });
      queryClient.invalidateQueries({
        queryKey: ["config-latest", entry.type, env],
      });
      toast.success(`Created ${entry.type} v${entry.version}`);
      reset();
      onCreated(entry.type);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setParseError(null);
    try {
      JSON.parse(payloadText);
    } catch {
      setParseError("Invalid JSON — fix the syntax before publishing.");
      return;
    }
    publishMutation.mutate();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New config</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-config-type">Config type</Label>
            <Input
              id="new-config-type"
              placeholder="e.g. backend_gateway_config"
              value={typeName}
              onChange={(e) => setTypeName(e.target.value)}
              className="font-mono text-sm"
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Environment</Label>
            <div className="flex items-center rounded-lg border p-0.5 w-fit">
              {(["staging", "prod"] as ConfigEnv[]).map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setEnv(e)}
                  className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${
                    env === e
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-config-payload">Payload (JSON)</Label>
            <textarea
              id="new-config-payload"
              value={payloadText}
              onChange={(e) => setPayloadText(e.target.value)}
              className="font-mono text-xs rounded-lg border bg-muted/30 p-3 resize-y h-48 w-full focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="{}"
              required
            />
            {parseError && (
              <p className="text-xs text-destructive">{parseError}</p>
            )}
          </div>

          <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
            <div className="flex flex-col gap-0.5">
              <Label htmlFor="new-config-public">Public</Label>
              <p className="text-xs text-muted-foreground">
                Exposes this version on the unauthenticated read endpoint.
              </p>
            </div>
            <Switch
              id="new-config-public"
              checked={isPublic}
              onCheckedChange={setIsPublic}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-config-commit">Commit message</Label>
            <Input
              id="new-config-commit"
              placeholder="Initial config"
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                publishMutation.isPending ||
                !typeName.trim() ||
                !commitMessage.trim()
              }
            >
              {publishMutation.isPending ? "Creating…" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function ConfigsPage() {
  const [envFilter, setEnvFilter] = useState<EnvFilter>("both");
  const [sheet, setSheet] = useState<SheetState>({ open: false });
  const [newConfigOpen, setNewConfigOpen] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: configTypes,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["config-types"],
    queryFn: () => configsProvider.listTypes(),
    staleTime: 60_000,
  });

  const showStaging = envFilter === "staging" || envFilter === "both";
  const showProd = envFilter === "prod" || envFilter === "both";
  const colSpan = 1 + (showStaging ? 1 : 0) + (showProd ? 1 : 0) + 1;

  return (
    <div className="flex flex-col h-full">
      <SiteHeader title="Configs" />

      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-2 lg:px-6 lg:pt-6">
        <div className="flex items-center rounded-lg border p-0.5">
          {(["staging", "prod", "both"] as EnvFilter[]).map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => setEnvFilter(filter)}
              className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${
                envFilter === filter
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {filter === "both"
                ? "Both"
                : filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: ["config-types"] })
            }
          >
            <IconRefresh className="size-3.5" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setNewConfigOpen(true)}>
            New config
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-4 pb-4 lg:px-6 lg:pb-6">
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                {showStaging && <TableHead>Staging</TableHead>}
                {showProd && <TableHead>Prod</TableHead>}
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: colSpan }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={colSpan}
                    className="py-12 text-center text-muted-foreground"
                  >
                    Failed to load configs. Check your connection and try again.
                  </TableCell>
                </TableRow>
              ) : !configTypes?.length ? (
                <TableRow>
                  <TableCell
                    colSpan={colSpan}
                    className="py-12 text-center text-muted-foreground"
                  >
                    No configs in the ledger yet.
                  </TableCell>
                </TableRow>
              ) : (
                configTypes.map((type) => (
                  <ConfigTypeRow
                    key={type}
                    configType={type}
                    envFilter={envFilter}
                    onSelect={() => setSheet({ open: true, configType: type })}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* New config dialog */}
      <NewConfigDialog
        open={newConfigOpen}
        onClose={() => setNewConfigOpen(false)}
        onCreated={(configType) => {
          setNewConfigOpen(false);
          setSheet({ open: true, configType });
        }}
      />

      {/* Detail sheet */}
      <Sheet
        open={sheet.open}
        onOpenChange={(open) => {
          if (!open) setSheet({ open: false });
        }}
      >
        <SheetContent
          side="right"
          className="sm:max-w-2xl w-full flex flex-col overflow-hidden p-0"
        >
          {sheet.open && (
            <ConfigDetailSheet
              configType={sheet.configType}
              envFilter={envFilter}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
